
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Calendar, DollarSign } from 'lucide-react';
import { TeacherMonthlyReport } from '@/hooks/useTeacherDetail';

interface TeacherMonthlyReportsProps {
  reports: TeacherMonthlyReport[];
}

export const TeacherMonthlyReports: React.FC<TeacherMonthlyReportsProps> = ({
  reports,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'ยืนยันแล้ว';
      case 'WAITING':
        return 'รอยืนยัน';
      case 'REJECTED':
        return 'ปฏิเสธ';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  };

  const getMonthName = (month: number) => {
    const monthNames = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return monthNames[month - 1] || month.toString();
  };

  if (reports.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            รายงานเงินเดือนรายเดือน
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-8">ไม่มีรายงานเงินเดือน</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          รายงานเงินเดือนรายเดือน ({reports.length} รายการ)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-medium">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  เดือน/ปี
                </th>
                <th className="text-left p-3 font-medium">จำนวนรายงาน</th>
                <th className="text-left p-3 font-medium">รายงานอนุมัติ</th>
                <th className="text-left p-3 font-medium">
                  <DollarSign className="h-4 w-4 inline mr-1" />
                  เงินเดือนรวม
                </th>
                <th className="text-left p-3 font-medium">คอมมิชชั่น</th>
                <th className="text-left p-3 font-medium">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium">
                      {getMonthName(report.month)} {report.year}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">
                      {report.total_reports}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs">
                      {report.approved_worksheet_reports}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <span className="font-medium">
                      {formatCurrency(report.total_salary)}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="font-medium">
                      {formatCurrency(report.commission_amount)}
                    </span>
                  </td>
                  <td className="p-3">
                    <Badge className={getStatusColor(report.confirmation_status)}>
                      {getStatusText(report.confirmation_status)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
